import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  adminPricingQueue,
  adminPricingApprove,
  adminPricingReject,
} from "../lib/api";

// The annual recheck parks CHANGED pricing in pricing_pending; this is the
// one-click old-vs-new review queue. Unchanged rechecks never reach here.
export default function AdminPricing() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminPricingQueue();
      setRows(d.tools || []);
    } catch {
      toast.error("could not load pricing queue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id, approve) => {
    try {
      await (approve ? adminPricingApprove(id) : adminPricingReject(id));
      toast.success(approve ? "pricing published" : "change discarded");
      load();
    } catch {
      toast.error("action failed");
    }
  };

  return (
    <div data-testid="admin-pricing">
      <div className="border border-black p-3 mb-4">
        <div className="text-[13px] font-bold mb-1">pricing changed — review queue</div>
        <p className="text-[12px] text-[#666]">
          The annual recheck re-scrapes each tool's pricing page; when it differs
          from what's published it lands here (it is NOT shown to users until you
          approve). Unchanged rechecks just refresh the "verified" date silently.
        </p>
      </div>
      {loading ? (
        <div className="text-[13px] text-[#888] py-6" data-testid="pricing-loading">loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-[13px] text-[#888] py-6" data-testid="pricing-empty">
          no pending pricing changes. ✓
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((t) => (
            <PricingRow key={t.id} tool={t} onAct={act} />
          ))}
        </div>
      )}
    </div>
  );
}

function PricingRow({ tool, onAct }) {
  const pending = tool.pricing_pending || {};
  return (
    <div className="border border-black p-3" data-testid={`pricing-row-${tool.slug}`}>
      <div className="flex items-baseline gap-2 flex-wrap mb-2">
        <span className="text-[14px] font-bold">{tool.title}</span>
        <a href={tool.url} target="_blank" rel="noopener noreferrer" className="tv-link text-[11px] ml-auto">{tool.url} ↗</a>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <PlanList label="current (live)" tier={tool.pricing} plans={tool.pricing_detail} />
        <PlanList label="scraped (new)" tier={pending.pricing} plans={pending.pricing_detail} highlight />
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <button className="tv-btn tv-btn-primary" onClick={() => onAct(tool.id, true)} data-testid="pricing-approve">[✓ publish new pricing]</button>
        <button className="tv-btn" onClick={() => onAct(tool.id, false)} data-testid="pricing-reject">[discard change]</button>
      </div>
    </div>
  );
}

function PlanList({ label, tier, plans, highlight }) {
  return (
    <div className={`border p-2 ${highlight ? "border-[#b07b00]" : "border-[#ddd]"}`}>
      <div className="text-[11px] uppercase tracking-wide text-[#888] mb-1">
        {label}{tier ? ` · ${tier}` : ""}
      </div>
      {Array.isArray(plans) && plans.length > 0 ? (
        <div className="text-[12px]">
          {plans.map((p, i) => (
            <div key={i} className="flex justify-between gap-3 py-0.5">
              <span>{p.plan || "—"}</span>
              <span className="font-bold whitespace-nowrap">{p.price || "—"}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-[12px] text-[#aaa]">(no plans)</div>
      )}
    </div>
  );
}
